import React from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import PropertiesOperator from '@/components/Form/components/Conditions/components/PropertiesOperator';
import { OperatorType } from '@/components/Form/components/Conditions/components/PropertiesOperator/utils';
import NumberInput from '@/components/NumberInput';
import { SortMenuProps } from '@/components/SortMenu/types';
import DateTagsAutoComplete from '@/modules/corporate/modules/tags/components/DateTagsAutoComplete';
import { FilterPropertyTypeEnum, RelativeDateFilterOperatorEnum } from '@/types/schema';

import { numberInputWidth } from './styles';

const getOperators = (t: TFunction) => [
  {
    id: RelativeDateFilterOperatorEnum.Next,
    label: t('Group:Operator.After'),
  },
  {
    id: RelativeDateFilterOperatorEnum.Last,
    label: t('Group:Operator.Before'),
  },
  {
    id: RelativeDateFilterOperatorEnum.This,
    label: t('Group:Operator.On'),
  },
];

interface ConditionalDueDateSelectorProps {
  value: number;
  onIntervalChange: (val: number) => void;
  operator: RelativeDateFilterOperatorEnum;
  onOperatorChange: (val: RelativeDateFilterOperatorEnum) => void;
  tagId?: string | null;
  onTagChange: (val?: string) => void;
  disabled?: boolean;
}

const OPERATOR_OVERRIDES: SortMenuProps['overrides'] = {
  PopoverContent: { maxWidth: 14 },
  MenuItem: {
    Typography: {
      variant: 'body-short-01',
    },
  },
};

function ConditionalDueDateSelector(props: ConditionalDueDateSelectorProps) {
  const { t } = useTranslation('Group');
  const { value, onIntervalChange, operator, onOperatorChange, tagId, onTagChange, disabled } =
    props;

  return (
    <Stack className="w-full pt-4 pb-12 ps-26" direction="col">
      <Stack gapX={8}>
        <NumberInput
          canBeEmpty
          className={numberInputWidth}
          disabled={disabled || operator === RelativeDateFilterOperatorEnum.This}
          inputSuffix={t('Common:day', { count: value })}
          max={999}
          min={0}
          value={value}
          onChange={onIntervalChange}
        />
        <PropertiesOperator
          disabled={disabled}
          operators={getOperators(t)}
          overrides={OPERATOR_OVERRIDES}
          propertyType={FilterPropertyTypeEnum.Tag}
          selectedOperator={operator}
          size="medium"
          onOperatorChange={(val: OperatorType) =>
            onOperatorChange(val as RelativeDateFilterOperatorEnum)
          }
        />
        <DateTagsAutoComplete
          disabled={disabled}
          selectedTagId={tagId || undefined}
          onChange={onTagChange}
        />
      </Stack>
      <Typography className="mt-8" color="text-text-secondary" variant="body-short-02">
        {t('AssignmentCreator:DueDateInfo')}
      </Typography>
    </Stack>
  );
}

export default ConditionalDueDateSelector;
